"use client"

import { useId, useMemo, useState } from "react"
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react"
import { Icon } from "@/shared/ui/icon"

/**
 * Цена (05-copy.md, блок E). Пакеты утверждены владельцем 08.09.2026
 * и живут в 00-facts.md, раздел «Утверждённые пакеты ресурсов».
 *
 * Две вкладки, а не два экрана: сравнение с подписками отвечает на другой
 * вопрос, чем таблица («зачем уходить», а не «сколько стоит»), и это
 * уточнение к цене, а не самостоятельная мысль. Вкладка ещё и режет
 * аудиторию правильно: человек со своими приложениями её не откроет.
 *
 * Чужих прайсов мы не считаем: во второй вкладке числа вводит посетитель.
 * Клиентских цен вендоров в фактах нет, а посчитанная за человека сумма
 * разойдётся с его собственным счётом на первом же уточнении.
 */

type Plan = {
  name: string
  specs: string
  price: number | null
}

const plans: Plan[] = [
  { name: "XS", specs: "0,5 vCPU, 512 МБ памяти, 5 ГБ диска", price: 25 },
  { name: "S", specs: "1 vCPU, 1 ГБ памяти, 10 ГБ диска", price: 45 },
  { name: "M", specs: "1 vCPU, 2 ГБ памяти, 20 ГБ диска", price: 80 },
  { name: "L", specs: "2 vCPU, 4 ГБ памяти, 40 ГБ диска", price: 150 },
  { name: "XL", specs: "4 vCPU, 8 ГБ памяти, 80 ГБ диска", price: 280 },
  {
    name: "Под задачу",
    specs: "Ресурсы считаем под ваше приложение, когда посмотрим на него",
    price: null,
  },
]

/** Пример счёта из 05-copy.md: Chatwoot (M) + Twenty (M) + Plausible (XS) = 185. */
const billRows = [
  { id: "chatwoot", app: "Chatwoot", role: "поддержка клиентов", plan: "M", price: 80 },
  { id: "twenty", app: "Twenty", role: "база клиентов", plan: "M", price: 80 },
  { id: "plausible", app: "Plausible", role: "аналитика сайта", plan: "XS", price: 25 },
  { id: "own", app: "Ваше приложение", role: "сайт или сервис", plan: "S", price: 45 },
]

const tabClass = (selected: boolean) =>
  [
    "cursor-pointer rounded-full px-5 py-2 text-sm font-medium transition",
    selected
      ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
      : "text-gray-600 hover:bg-slate-100 dark:text-white/60 dark:hover:bg-white/10",
  ].join(" ")

const inputClass =
  "h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-slate-900 dark:border-slate-300"

export default function Pricing() {
  const [selected, setSelected] = useState<string[]>(["chatwoot", "twenty", "plausible"])
  const [people, setPeople] = useState("")
  const [services, setServices] = useState("")
  const [perPerson, setPerPerson] = useState("")

  const fieldId = useId()

  const total = useMemo(
    () =>
      billRows
        .filter((row) => selected.includes(row.id))
        .reduce((sum, row) => sum + row.price, 0),
    [selected],
  )

  const subscription = useMemo(() => {
    const p = Number(people.replace(",", "."))
    const s = Number(services.replace(",", "."))
    const c = Number(perPerson.replace(",", "."))
    if (!p || !s || !c) return null
    const now = p * s * c
    return { now, later: (p + 5) * s * c }
  }, [people, services, perPerson])

  const toggle = (id: string) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    )

  const format = (value: number) =>
    new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(value)

  return (
    <TabGroup>
      <TabList className="flex flex-wrap gap-2 rounded-full bg-slate-100 p-1 sm:inline-flex dark:bg-white/[0.06]">
        <Tab className={({ selected }) => tabClass(selected)}>Пакеты</Tab>
        <Tab className={({ selected }) => tabClass(selected)}>
          А если сравнить с подписками
        </Tab>
      </TabList>

      <TabPanels className="mt-10">
        <TabPanel>
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {plans.map((plan) => (
              <li
                key={plan.name}
                className={
                  plan.price === null
                    ? "rounded-2xl border border-dashed border-slate-300 p-6 dark:border-white/20"
                    : "rounded-2xl bg-white p-6 ring-1 ring-black/5 dark:bg-white/[0.03] dark:ring-white/10"
                }
              >
                <p className="type-overline text-brand-700 dark:text-jungle-300">
                  {plan.name}
                </p>
                <p className="mt-4 text-slate-900 dark:text-white">
                  {plan.price === null ? (
                    <span className="text-title">По расчёту</span>
                  ) : (
                    <>
                      <span className="type-numeric text-title">{plan.price}</span>
                      <span className="ml-1.5 text-sm text-gray-500 dark:text-white/50">
                        BYN в месяц
                      </span>
                    </>
                  )}
                </p>
                <p className="mt-4 text-sm text-gray-600 dark:text-white/60">
                  {plan.specs}
                </p>
              </li>
            ))}
          </ul>

          <div className="mt-12 grid gap-10 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-7">
              <h3 className="text-subhead text-slate-900 dark:text-white">
                Так выглядит счёт компании
              </h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-white/60">
                Уберите лишнее или добавьте своё приложение, сумма пересчитается.
              </p>

              <ul className="mt-6 space-y-2">
                {billRows.map((row) => {
                  const checked = selected.includes(row.id)
                  return (
                    <li key={row.id}>
                      <button
                        type="button"
                        aria-pressed={checked}
                        onClick={() => toggle(row.id)}
                        className={[
                          "flex w-full cursor-pointer items-center gap-4 rounded-2xl px-4 py-3.5 text-left transition",
                          checked
                            ? "bg-white ring-1 ring-black/5 dark:bg-white/[0.06] dark:ring-white/10"
                            : "opacity-55 ring-1 ring-transparent hover:opacity-80",
                        ].join(" ")}
                      >
                        <Icon
                          name={
                            checked
                              ? "check-circle-bold-duotone"
                              : "close-circle-bold-duotone"
                          }
                          aria-hidden="true"
                          className={
                            checked
                              ? "size-5 shrink-0 text-brand-600 dark:text-jungle-400"
                              : "size-5 shrink-0 text-gray-400 dark:text-white/40"
                          }
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block font-medium text-slate-900 dark:text-white">
                            {row.app}
                          </span>
                          <span className="block text-sm text-gray-500 dark:text-white/50">
                            {row.role}
                          </span>
                        </span>
                        <span className="type-numeric shrink-0 text-sm text-gray-600 dark:text-white/60">
                          {row.plan} · {row.price}
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>

              <p
                className="mt-6 flex items-baseline gap-3 border-t border-black/5 pt-6 dark:border-white/10"
                aria-live="polite"
              >
                <span className="text-base text-gray-600 dark:text-white/60">
                  Итого в месяц
                </span>
                <span className="type-numeric text-title text-slate-900 dark:text-white">
                  {total === 0 ? "0" : format(total)} BYN
                </span>
              </p>
              {total === 0 ? (
                <p className="mt-2 text-sm text-gray-500 dark:text-white/50">
                  Выберите хотя бы одно приложение, чтобы увидеть сумму.
                </p>
              ) : null}
            </div>

            <div className="space-y-5 text-base text-pretty text-gray-600 lg:col-span-5 dark:text-white/60">
              <p>
                Домены и HTTPS, копии, мониторинг и логи, установка из каталога,
                обновления по вашему запросу и дежурный инженер уже в цене пакета.
                Отдельного счёта за обслуживание нет.
              </p>
              <p>
                Разовые работы по переносу считаем после разбора, от бесплатно до
                стоимости времени команды. Числа не будет, пока мы не посмотрели, что
                переносим.
              </p>
            </div>
          </div>
        </TabPanel>

        <TabPanel>
          <div className="grid gap-10 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-5">
              <p className="text-base text-pretty text-gray-600 dark:text-white/60">
                Чужие прайсы мы за вас не выдумываем. Подставьте свои числа и посмотрите,
                что будет со счётом, когда в компании станет на пять человек больше.
              </p>

              <div className="mt-8 space-y-5">
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor={`${fieldId}-people`}
                    className="text-sm font-semibold text-slate-900 dark:text-white"
                  >
                    Сколько у вас сотрудников
                  </label>
                  <input
                    id={`${fieldId}-people`}
                    inputMode="numeric"
                    value={people}
                    onChange={(event) => setPeople(event.target.value)}
                    className={inputClass}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label
                    htmlFor={`${fieldId}-services`}
                    className="text-sm font-semibold text-slate-900 dark:text-white"
                  >
                    Сколько сервисов оплачиваете
                  </label>
                  <input
                    id={`${fieldId}-services`}
                    inputMode="numeric"
                    value={services}
                    onChange={(event) => setServices(event.target.value)}
                    className={inputClass}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label
                    htmlFor={`${fieldId}-price`}
                    className="text-sm font-semibold text-slate-900 dark:text-white"
                  >
                    Сколько платите за человека в месяц
                  </label>
                  <input
                    id={`${fieldId}-price`}
                    inputMode="decimal"
                    value={perPerson}
                    onChange={(event) => setPerPerson(event.target.value)}
                    className={inputClass}
                  />
                  <p className="text-sm text-gray-500 dark:text-white/50">
                    В той валюте, в которой приходит ваш счёт.
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-3xl bg-white p-8 ring-1 ring-black/5 sm:p-10 dark:bg-white/[0.03] dark:ring-white/10">
                {subscription ? (
                  <dl className="grid gap-8 sm:grid-cols-2" aria-live="polite">
                    <div>
                      <dt className="text-sm text-gray-500 dark:text-white/50">
                        Подписки сейчас
                      </dt>
                      <dd className="type-numeric text-title mt-2 text-slate-900 dark:text-white">
                        {format(subscription.now)}
                      </dd>
                      <dd className="mt-1 text-sm text-gray-500 dark:text-white/50">
                        в месяц
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500 dark:text-white/50">
                        Подписки, когда людей станет на пять больше
                      </dt>
                      <dd className="type-numeric text-title mt-2 text-slate-900 dark:text-white">
                        {format(subscription.later)}
                      </dd>
                      <dd className="mt-1 text-sm text-gray-500 dark:text-white/50">
                        в месяц, плюс {format(subscription.later - subscription.now)}
                      </dd>
                    </div>
                  </dl>
                ) : (
                  <p className="text-base text-gray-500 dark:text-white/50">
                    Заполните три поля слева, и здесь появятся два числа: ваш счёт сейчас
                    и он же после найма ещё пяти человек.
                  </p>
                )}

                <p className="mt-8 border-t border-black/5 pt-8 text-base text-pretty text-slate-900 dark:border-white/10 dark:text-white/85">
                  Пакет ресурсов от числа сотрудников не зависит. Приложению всё равно,
                  сколько людей в него зашло.
                </p>
              </div>

              <p className="mt-6 text-base text-pretty text-gray-600 dark:text-white/60">
                Открытый аналог редко повторяет платный сервис один в один. Что именно
                теряется в вашем случае, инженер скажет до того, как вы что-то поставите,
                а не после.
              </p>
            </div>
          </div>
        </TabPanel>
      </TabPanels>
    </TabGroup>
  )
}
